<div class="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 p-6">
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="mb-8 flex items-center justify-between">
      <div>
        <button (click)="goBack()" class="flex items-center text-slate-600 hover:text-slate-900 mb-4 transition-colors">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18">
            </path>
          </svg>
          Back to Dashboard
        </button>
        <h1 class="text-4xl font-bold text-slate-900 mb-2">
          Full Analytics Report
        </h1>
        <p class="text-slate-600">Comprehensive insights into your chatbot performance</p>
      </div>
      <div class="flex gap-3 items-center">
        <select [(ngModel)]="selectedRange" (change)="onRangeChange()"
          class="px-4 py-3 bg-white border border-slate-300 rounded-xl font-semibold focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all">
          <option *ngFor="let range of dateRanges" [value]="range.value">{{range.label}}</option>
        </select>
        <button (click)="exportData()"
          class="px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all duration-300 flex items-center shadow-sm">
          <svg class="w-5 h-5 mr-2 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
          </svg>
          JSON
        </button>
        <button (click)="exportPdf()"
          class="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
            </path>
          </svg>
          Export PDF
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="flex items-center justify-center py-20">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent">
        </div>
        <p class="mt-4 text-slate-600 font-semibold">Loading analytics...</p>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="error && !loading" class="bg-red-50 border border-red-200 rounded-xl p-6 text-red-800">
      <div class="flex items-center">
        <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <div class="font-semibold">Error Loading Analytics</div>
          <div class="text-sm">{{error}}</div>
        </div>
      </div>
    </div>

    <!-- Analytics Content -->
    <div *ngIf="!loading && !error" class="space-y-6">
      <!-- Key Metrics Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Total Conversations -->
        <div
          class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500">
          <div class="flex items-center justify-between mb-4">
            <div
              class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
                </path>
              </svg>
            </div>
            <div class="text-right">
              <div class="text-sm text-slate-500 font-medium">{{selectedRange === 1 ? 'Today' : 'Last ' + selectedRange
                + ' Days'}}</div>
              <div class="text-3xl font-bold text-slate-900">{{(selectedRange === 1 ? summary.today.total_conversations
                : rangeTotals?.total_conversations) || 0}}</div>
            </div>
          </div>
          <div class="text-xs text-slate-500 font-medium uppercase">Total Conversations</div>
          <div class="text-sm text-slate-600 mt-2">All-time: <span
              class="font-bold text-blue-600">{{summary.totals.total_conversations || 0}}</span></div>
        </div>

        <!-- Total Messages -->
        <div
          class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-emerald-500">
          <div class="flex items-center justify-between mb-4">
            <div
              class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z">
                </path>
              </svg>
            </div>
            <div class="text-right">
              <div class="text-sm text-slate-500 font-medium">{{selectedRange === 1 ? 'Today' : 'Last ' + selectedRange
                + ' Days'}}</div>
              <div class="text-3xl font-bold text-slate-900">{{(selectedRange === 1 ? summary.today.total_messages :
                rangeTotals?.total_messages) || 0}}</div>
            </div>
          </div>
          <div class="text-xs text-slate-500 font-medium uppercase">Total Messages</div>
          <div class="text-sm text-slate-600 mt-2">All-time: <span
              class="font-bold text-emerald-600">{{summary.totals.total_messages || 0}}</span></div>
        </div>

        <!-- FAQ Matches -->
        <div
          class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500">
          <div class="flex items-center justify-between mb-4">
            <div
              class="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                </path>
              </svg>
            </div>
            <div class="text-right">
              <div class="text-sm text-slate-500 font-medium">{{selectedRange === 1 ? 'Today' : 'Last ' + selectedRange
                + ' Days'}}</div>
              <div class="text-3xl font-bold text-slate-900">{{(selectedRange === 1 ? summary.today.faq_matches :
                rangeTotals?.faq_matches) || 0}}</div>
            </div>
          </div>
          <div class="text-xs text-slate-500 font-medium uppercase">FAQ Matches</div>
          <div class="text-sm text-slate-600 mt-2">All-time: <span
              class="font-bold text-purple-600">{{summary.totals.faq_matches || 0}}</span></div>
        </div>

        <!-- AI Responses -->
        <div
          class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-amber-500">
          <div class="flex items-center justify-between mb-4">
            <div
              class="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
                </path>
              </svg>
            </div>
            <div class="text-right">
              <div class="text-sm text-slate-500 font-medium">{{selectedRange === 1 ? 'Today' : 'Last ' + selectedRange
                + ' Days'}}</div>
              <div class="text-3xl font-bold text-slate-900">{{(selectedRange === 1 ? summary.today.ai_responses :
                rangeTotals?.ai_responses) || 0}}</div>
            </div>
          </div>
          <div class="text-xs text-slate-500 font-medium uppercase">AI Responses</div>
          <div class="text-sm text-slate-600 mt-2">All-time: <span
              class="font-bold text-amber-600">{{summary.totals.ai_responses || 0}}</span></div>
        </div>
      </div>

      <!-- Performance Insights -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- FAQ Effectiveness -->
        <div class="bg-white rounded-2xl shadow-lg p-6">
          <h3 class="text-lg font-bold text-slate-800 mb-4">FAQ Effectiveness</h3>
          <div class="relative pt-1">
            <div class="flex mb-2 items-center justify-between">
              <div>
                <span class="text-xs font-semibold inline-block text-purple-600">
                  {{faqEffectiveness}}%
                </span>
              </div>
            </div>
            <div class="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-purple-100">
              <div [style.width.%]="faqEffectiveness"
                class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500">
              </div>
            </div>
          </div>
          <p class="text-sm text-slate-600">Queries resolved by FAQs without AI</p>
        </div>

        <!-- Avg Messages per Conversation -->
        <div class="bg-white rounded-2xl shadow-lg p-6">
          <h3 class="text-lg font-bold text-slate-800 mb-4">Avg Messages/Conversation</h3>
          <div class="text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
            {{avgMessagesPerConvo}}
          </div>
          <p class="text-sm text-slate-600">Messages exchanged per conversation</p>
        </div>

        <!-- Sentiment Analysis -->
        <div class="bg-white rounded-2xl shadow-lg p-6">
          <h3 class="text-lg font-bold text-slate-800 mb-4">User Sentiment</h3>
          <div class="text-5xl font-bold mb-2" [ngClass]="sentimentColor">
            {{math.round((selectedRange === 1 ? summary.today.avg_sentiment_score : rangeAvgSentiment) * 100) || 0}}%
          </div>
          <p class="text-sm font-semibold mb-1" [ngClass]="sentimentColor">{{sentimentLabel}}</p>
          <p class="text-sm text-slate-600">{{selectedRange === 1 ? 'Average sentiment score today' : 'Average sentiment
            score for period'}}</p>
        </div>
      </div>

      <!-- Trends Table -->
      <div class="bg-white rounded-2xl shadow-lg p-6">
        <h3 class="text-xl font-bold text-slate-800 mb-6">Daily Trends (Last {{selectedRange}} Days)</h3>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-200">
                <th class="text-left py-3 px-4 text-sm font-semibold text-slate-700">Date</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-slate-700">Conversations</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-slate-700">Messages</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-slate-700">FAQ Matches</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-slate-700">AI Responses</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-slate-700">Avg Sentiment</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let trend of trends; let i = index"
                class="border-b border-slate-100 hover:bg-slate-50 transition-colors" [class.bg-blue-50]="i === 0">
                <td class="py-3 px-4 text-sm font-medium text-slate-900">
                  {{trend.date}}
                  <span *ngIf="i === 0" class="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">Latest</span>
                </td>
                <td class="py-3 px-4 text-sm text-right text-slate-700">{{trend.total_conversations}}</td>
                <td class="py-3 px-4 text-sm text-right text-slate-700">{{trend.total_messages}}</td>
                <td class="py-3 px-4 text-sm text-right text-slate-700">{{trend.faq_matches}}</td>
                <td class="py-3 px-4 text-sm text-right text-slate-700">{{trend.ai_responses}}</td>
                <td class="py-3 px-4 text-sm text-right">
                  <span [ngClass]="{
                    'text-emerald-600 font-semibold': (trend.avg_sentiment_score || 0) >= 0.7,
                    'text-green-600': (trend.avg_sentiment_score || 0) >= 0.5 && (trend.avg_sentiment_score || 0) < 0.7,
                    'text-amber-600': (trend.avg_sentiment_score || 0) >= 0.3 && (trend.avg_sentiment_score || 0) < 0.5,
                    'text-red-600': (trend.avg_sentiment_score || 0) < 0.3
                  }">
                    {{math.round((trend.avg_sentiment_score || 0) * 100)}}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Insights & Recommendations -->
      <div class="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl shadow-lg p-6 border border-blue-200">
        <h3 class="text-xl font-bold text-slate-800 mb-4 flex items-center">
          <svg class="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
            </path>
          </svg>
          AI-Powered Insights
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="bg-white rounded-xl p-4 shadow-sm">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-emerald-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
              </svg>
              <div>
                <div class="font-semibold text-slate-800 mb-1">Strong FAQ Coverage</div>
                <div class="text-sm text-slate-600">Your FAQ database is effectively handling most queries. Consider
                  expanding it further.</div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-4 shadow-sm">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div class="font-semibold text-slate-800 mb-1">Peak Hours Detected</div>
                <div class="text-sm text-slate-600">Most activity occurs between 9 AM - 5 PM. Consider staffing support
                  accordingly.</div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-4 shadow-sm">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-purple-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div class="font-semibold text-slate-800 mb-1">Positive User Experience</div>
                <div class="text-sm text-slate-600">High sentiment scores indicate users are satisfied with responses.
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-4 shadow-sm">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-amber-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z">
                </path>
              </svg>
              <div>
                <div class="font-semibold text-slate-800 mb-1">AI Fallback Usage</div>
                <div class="text-sm text-slate-600">Monitor AI responses for quality and add popular questions to FAQs.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>